import React, { useMemo, useState } from 'react';
import ModalArticles from '../../composants/ModalArticles/ModalArticles';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import { Article } from '../../services/articlesService';
import { LotTechnique } from '../../composants/LotCard/LotCard';

interface CatalogueLotModalProps {
  lot: LotTechnique;
  // Lot geographique retenu par la page (filtre, initialise sur le departement du siege social).
  // Il determine le titulaire dont on affiche les articles et les prix. Vide = aucun lot resolu.
  geo: string;
  // Libelle du lot geographique ("13 - Bouches-du-Rhône") pour le titre : la modale n'a que l'id.
  geoLabel?: string;
  onClose: () => void;
}

// Reference STABLE pour `existingSelection` : le defaut `= []` du composant creerait un nouveau tableau a
// chaque render -> l'useEffect de chargement (deps: existingSelection) boucle.
const EMPTY_SELECTION: Article[] = [];

// Lots dont la consultation initiale est encore en cours : ils n'ont AUCUN titulaire, dans aucun
// departement. A distinguer d'un lot infructueux, qui a bien ete consulte mais reste sans titulaire
// (parfois seulement sur certains departements). Les deux donnent un catalogue vide, pas la meme cause.
// Liste metier, tenue a la main : un lot en sort le jour ou son accord-cadre est attribue.
const LOTS_CONSULTATION_EN_COURS = ['114', '213', '301'];

// Un catalogue vide veut toujours dire "aucun titulaire sur ce lot x ce departement" : tout lot_contrat
// porte des prix. Reste a dire POURQUOI il n'y en a pas.
const messageCatalogueVide = (code: string): string =>
  LOTS_CONSULTATION_EN_COURS.includes(String(code))
    ? 'La consultation pour ce lot est en cours, nous vous invitons à patienter quelques semaines.'
    : 'Nous sommes désolés, ce lot est infructueux à ce jour. Une nouvelle consultation pour ce lot est en cours, nous vous invitons à patienter quelques semaines.';

// Consultation du catalogue d'un lot, depuis la page Catalogue acheteur. Deux etapes :
//
//  1. CONSULTATION (ModalArticles en mode "browse" : ni quantite ni total) : l'acheteur voit les
//     articles du titulaire rang 1 du lot (technique + geographique), leurs prix et leurs paliers via
//     "Details". Aucune selection n'est transmise (`ignoreSelection`). Le lot geographique n'est pas
//     choisi ici — il vient de la page (filtre, pre-selectionne sur le departement du siege social).
//
//  2. Un clic sur une ligne (hors "Details") ou le bouton "Nouveau Bon de commande" -> creation d'un BC ;
//     le bouton "Nouveau Marche subsequent" -> creation d'un MS. Les deux ouvrent LancerMarcheModal avec
//     le lot + le departement PRE-REMPLIS (l'acheteur confirme). L'operation est creee vide, puis remplie
//     dans le suivi.
const CatalogueLotModal: React.FC<CatalogueLotModalProps> = ({ lot, geo, geoLabel, onClose }) => {
  // Etape 2 : la modale de creation (apres clic ligne / bouton). BC ou MS selon le bouton choisi.
  const [createMode, setCreateMode] = useState<'BC' | 'MS' | null>(null);

  // Titre : lot technique ET lot geographique (le prix depend du titulaire du couple des deux).
  const lotTechniqueLabel = `${lot.code} - ${lot.nom}`;
  const titre = geoLabel ? `${lotTechniqueLabel}, ${geoLabel}` : lotTechniqueLabel;

  // Commande synthetique (memoisee : une nouvelle reference a chaque render relancerait le chargement de
  // ModalArticles en boucle). `_catalogueLotQuery` -> le back resout le titulaire rang 1 et renvoie ses
  // prix en UN appel. id_lot_departement / id_lot_technique servent aussi aux references (extractDepartementInfo).
  const commandes = useMemo(
    () =>
      geo
        ? [
            {
              _catalogueLotQuery: { id_lot_technique: lot.id, id_departement: Number(geo) },
              id_lot_departement: Number(geo),
              id_lot_technique: lot.id,
            },
          ]
        : [],
    [geo, lot.id],
  );

  if (createMode) {
    return (
      <LancerMarcheModal
        isOpen
        onClose={onClose}
        title={createMode === 'BC' ? 'Nouveau Bon de Commande' : 'Lancer un marché subséquent'}
        showNomOperation
        showLotTechnique
        showLotGeographique
        showDepartement={false}
        mode={createMode}
        initialValues={{ lotTechnique: String(lot.id), lotGeographique: geo }}
      />
    );
  }

  return (
    <ModalArticles
      isOpen
      commandes={commandes}
      lotNom={lotTechniqueLabel}
      title={titre}
      onAddToCommand={() => {}}
      existingSelection={EMPTY_SELECTION}
      prestataireRang={1}
      ignoreSelection
      onNouveauBonCommande={() => setCreateMode('BC')}
      onNouveauMarcheSubsequent={() => setCreateMode('MS')}
      awaitingSelection={!geo}
      emptyMessage={messageCatalogueVide(lot.code)}
      onClose={onClose}
    />
  );
};

export default CatalogueLotModal;
